import { useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useSelector } from '../../../Flux/index.js'
import { selectCreatingProject, selectCurrentProject } from '../../../Flux/Selector/Project/ProjectSelectors.js'
import Actions from '../../../Flux/index.js'
import { projectSlice } from '../../../Flux/Slice/Project/ProjectSlice.js'
import { taxaOptions } from '../HomePage/01-CreateOrderPage/TaxaOptions.tsx'
import type { BaseModalProps } from '../../Modal/BaseModal.js'
import { BaseModal } from '../../Modal/BaseModal.js'
import type { RedlistModeKey } from '../../../Generated/RedlistMode.ts'
import { RedlistModes } from '../../../Generated/RedlistMode.ts'
import type { ProcessingTypeKey } from '../../../Generated/ProcessingType.ts'
import { ProcessingTypes } from '../../../Generated/ProcessingType.ts'

export const redlistOptions = Object.values(RedlistModes)

export const CreateProjectModal = ({ isOpen, onClose }: BaseModalProps<string>) => {
  const dispatch = useDispatch()
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [targetTaxonomy, setTargetTaxonomy] = useState<string | null>(null)
  const [redlistMode, setRedlistMode] = useState<RedlistModeKey | null>(null)
  const [processingType, setProcessingType] = useState<ProcessingTypeKey | null>(null)
  const [submitted, setSubmitted] = useState(false)

  const currentProject = useSelector(selectCurrentProject)
  const creating = useSelector(selectCreatingProject)
  /* The id in flight when the form was submitted, so a failed create cannot be read as this one. */
  const idBeforeSubmit = useRef<string | undefined>(undefined)

  useEffect(() => {
    if (isOpen) {
      setSubmitted(false)
      setName('')
      setDescription('')
      setTargetTaxonomy(null)
      setRedlistMode(null)
      setProcessingType(null)
    }
  }, [isOpen])

  const handleSubmit = () => {
    // TODO: form validation
    if (!name.trim()) {
      // Name is required
      return
    }

    idBeforeSubmit.current = currentProject?.id
    setSubmitted(true)
    dispatch(
      Actions[projectSlice.name].createProjectRequest({
        name,
        description,
        ...(targetTaxonomy && { targetTaxonomy }),
        redlistMode: redlistMode ?? undefined,
        processingType: processingType ?? undefined,
      })
    )
  }

  /*
   * The modal used to close on dispatch, which meant a failed create looked exactly like a successful
   * one. It now stays up until the request settles, and hands the new project's id back so the caller
   * can open it. A failure leaves the form filled in rather than discarding what was typed.
   */
  useEffect(() => {
    if (!submitted || creating) return
    setSubmitted(false)
    const created = currentProject?.id !== idBeforeSubmit.current ? currentProject?.id : undefined
    if (created) onClose(created)
  }, [submitted, creating, currentProject, onClose])

  const resetForm = () => {
    setName('')
    setDescription('')
    setTargetTaxonomy(null)
    setProcessingType(null)
  }

  return (
    <BaseModal isOpen={isOpen} onClose={onClose}>
      <div className={'modal-box w-fit'}>
        <h3 className="text-lg font-bold">Create New Project</h3>
        <div className="form-control w-full">
          <label className="label">
            <span className="label-text">Project Name</span>
          </label>
          <input
            data-testid={'project-name'}
            type="text"
            placeholder="Enter project name"
            className="input input-bordered w-full"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <div className="form-control w-full mt-4">
          <label className="label">
            <span className="label-text">Description</span>
          </label>
          <textarea
            placeholder="Enter project description"
            className="textarea textarea-bordered w-full"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>
        <div className="form-control w-full mt-4">
          <label className="label">
            <span className="label-text">
              Target Taxonomy
              <br />
              <span className={'text-xs'}>(Optional, used to determine default evaluation settings)</span>
            </span>
          </label>
          <select
            id="taxa"
            className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            onChange={(e) => setTargetTaxonomy(e.target.value || null)}
          >
            {[{ value: undefined, label: 'None', disabled: false }, ...taxaOptions].map((option) => (
              <option key={option.value} value={option.value} disabled={option.disabled}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
        <div className="form-control w-full mt-4">
          <label className="label">
            <span className="label-text">Redlist Mode</span>
          </label>
          <select
            id="redlistMode"
            className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            value={redlistMode || ''}
            onChange={(e) => setRedlistMode((e.target.value as RedlistModeKey) || null)}
          >
            <option value="">Organization Default</option>
            {redlistOptions.map((option) => (
              <option key={option.key} value={option.key}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
        <div className="form-control w-full mt-4">
          <label className="label">
            <span className="label-text">Processing Type</span>
          </label>
          <select
            id="processingType"
            className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            value={processingType || ''}
            onChange={(e) => setProcessingType((e.target.value as ProcessingTypeKey) || null)}
          >
            <option value="">Any</option>
            {Object.values(ProcessingTypes).map((option) => (
              <option key={option.key} value={option.key}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
        <div className={'modal-action'}>
          <button
            className="btn btn-ghost mr-2"
            onClick={() => {
              resetForm()
              onClose()
            }}
          >
            Cancel
          </button>
          <button
            data-testid={'create-project-button'}
            className={`btn btn-primary`}
            onClick={handleSubmit}
            disabled={!name.trim() || creating}
          >
            {creating ? 'Creating…' : 'Create Project'}
          </button>
        </div>
      </div>
    </BaseModal>
  )
}
